import {
  IScheduleHandler,
  ScheduleContext,
  ScheduleEvaluationResult,
} from "../types/index";
import {
  Playlist,
  ScheduleType,
  PrayerBasedSchedule,
  isPrayerBasedSchedule,
} from "../types";
import { getPrayerLength } from "../utils/prayerTimeUtils";

/**
 * Prayer-Based Schedule Handler
 *
 * Handles playlists with ScheduleType.PrayerBased (7)
 * Active before, after, or between specific prayers with flexible offsets.
 *
 * This is the CRITICAL PLAYLIST handler (Priority = 5)
 * Used for salah announcements, countdown timers, and prayer-specific content.
 *
 * Modes:
 * - "before": Active X seconds before prayer starts
 * - "after": Active X seconds after prayer starts
 * - "during-adhan": Active during adhan window
 * - "after-adhan": Active from adhan end until prayer/jamaah time
 * - "between": Active between two prayers (same as PrayerWindow)
 */
export class PrayerBasedScheduleHandler implements IScheduleHandler {
  public readonly scheduleType = ScheduleType.PrayerBased;

  /**
   * Evaluate if playlist is active based on prayer-based schedule
   */
  evaluate(
    playlist: Playlist,
    context: ScheduleContext,
  ): ScheduleEvaluationResult {
    const schedule = playlist.schedule;

    if (!schedule || !isPrayerBasedSchedule(schedule)) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Invalid or missing PrayerBased schedule configuration",
      };
    }

    if (!context.prayerTimes) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Prayer times not available",
      };
    }

    const currentDay = context.currentTime.getDay();

    // ✅ Only check daysOfWeek for "between" mode
    // For "before" and "after", empty daysOfWeek means "all days"
    if (schedule.prayerScheduleType === "between") {
      if (!schedule.daysOfWeek || !schedule.daysOfWeek.includes(currentDay)) {
        return {
          isActive: false,
          effectivePriority: playlist.priority,
          reason: `Not active on ${this.getDayName(currentDay)}`,
        };
      }
    } else if (schedule.daysOfWeek && schedule.daysOfWeek.length > 0) {
      // ✅ For "before" and "after", only check if daysOfWeek is explicitly set
      if (!schedule.daysOfWeek.includes(currentDay)) {
        return {
          isActive: false,
          effectivePriority: playlist.priority,
          reason: `Not active on ${this.getDayName(currentDay)}`,
        };
      }
    }

    // Evaluate based on prayer schedule type
    switch (schedule.prayerScheduleType) {
      case "before":
        return this.evaluateBefore(playlist, schedule, context);

      case "after":
        return this.evaluateAfter(playlist, schedule, context);

      case "during-adhan":
        return this.evaluateAdhanWindow(playlist, schedule, context, "during");

      case "after-adhan":
        return this.evaluateAdhanWindow(playlist, schedule, context, "after");

      case "between":
        return this.evaluateBetween(playlist, schedule, context);

      default:
        return {
          isActive: false,
          effectivePriority: playlist.priority,
          reason: `Unknown prayer schedule type: ${schedule.prayerScheduleType}`,
        };
    }
  }

  /**
   * Evaluate "before" prayer mode
   */
  private evaluateBefore(
    playlist: Playlist,
    schedule: PrayerBasedSchedule,
    context: ScheduleContext,
  ): ScheduleEvaluationResult {
    // Check each prayer in the list
    for (const prayerName of schedule.prayers) {
      const prayerTime = this.getPrayerTime(context.prayerTimes!, prayerName);

      if (!prayerTime) {
        continue;
      }

      // Jummah prayers only activate on Fridays (day === 5)
      if (
        this.isJummahPrayer(prayerName) &&
        context.currentTime.getDay() !== 5
      ) {
        continue;
      }

      // Dhuhr prayers only activate on non-Fridays (Jummah replaces Dhuhr on Friday)
      if (
        this.isDhuhrPrayer(prayerName) &&
        context.currentTime.getDay() === 5
      ) {
        continue;
      }

      const secondsBefore = schedule.secondsBeforeStartPrayer || 0;
      const displayDuration = schedule.displayDurationSeconds || 0;
      const windowStart = new Date(prayerTime);
      windowStart.setSeconds(windowStart.getSeconds() - secondsBefore);
      // If displayDurationSeconds is set, cap the window so the playlist
      // doesn't stay active for the entire secondsBeforeStartPrayer period
      const windowEnd =
        displayDuration > 0
          ? new Date(
              Math.min(
                windowStart.getTime() + displayDuration * 1000,
                prayerTime.getTime(),
              ),
            )
          : prayerTime;

      const nowMs = context.currentTime.getTime();
      const diffToStart = Math.round((windowStart.getTime() - nowMs) / 1000);
      const diffToEnd = Math.round((windowEnd.getTime() - nowMs) / 1000);
      console.log(
        `⏱️ [PrayerBased:before] "${playlist.name}" → ${prayerName} | prayerTime=${prayerTime.toLocaleTimeString()} | windowStart=${windowStart.toLocaleTimeString()} windowEnd=${windowEnd.toLocaleTimeString()} | secondsBefore=${secondsBefore} displayDuration=${displayDuration} | now→start=${diffToStart}s now→end=${diffToEnd}s | inWindow=${context.currentTime >= windowStart && context.currentTime <= windowEnd}`,
      );

      // Check if currently in this prayer's window
      if (
        context.currentTime >= windowStart &&
        context.currentTime <= windowEnd
      ) {
        return {
          isActive: true,
          effectivePriority: playlist.priority,
          deactivationTime: windowEnd,
          reason: `Active before ${prayerName} (${secondsBefore}s before)`,
          nextEvaluationTime: windowEnd,
        };
      }
    }

    return {
      isActive: false,
      effectivePriority: playlist.priority,
      reason: "Not currently before any scheduled prayer",
    };
  }

  /**
   * Evaluate "after" prayer mode
   */
  private evaluateAfter(
    playlist: Playlist,
    schedule: PrayerBasedSchedule,
    context: ScheduleContext,
  ): ScheduleEvaluationResult {
    // Check each prayer in the list
    for (const prayerName of schedule.prayers) {
      const prayerTime = this.getPrayerTime(context.prayerTimes!, prayerName);

      if (!prayerTime) {
        continue;
      }

      // Jummah prayers only activate on Fridays (day === 5)
      if (
        this.isJummahPrayer(prayerName) &&
        context.currentTime.getDay() !== 5
      ) {
        continue;
      }

      // Dhuhr prayers only activate on non-Fridays (Jummah replaces Dhuhr on Friday)
      if (
        this.isDhuhrPrayer(prayerName) &&
        context.currentTime.getDay() === 5
      ) {
        continue;
      }

      const afterPhase = schedule.afterPhase || "after-end";
      const secondsAfterStart = schedule.secondsAfterStartPrayer || 0;
      const displayDuration = schedule.displayDurationSeconds || 0;

      // Default behavior remains after prayer end for backward compatibility.
      const windowStart = this.getAfterWindowStart(
        prayerTime,
        prayerName,
        afterPhase,
        secondsAfterStart,
        context,
      );

      const windowEnd = new Date(windowStart);
      windowEnd.setSeconds(windowEnd.getSeconds() + displayDuration);

      const nowMs = context.currentTime.getTime();
      const diffToStart = Math.round((windowStart.getTime() - nowMs) / 1000);
      const diffToEnd = Math.round((windowEnd.getTime() - nowMs) / 1000);

      // Check if currently in this prayer's window
      if (
        context.currentTime >= windowStart &&
        context.currentTime <= windowEnd
      ) {
        return {
          isActive: true,
          effectivePriority: playlist.priority,
          deactivationTime: windowEnd,
          reason:
            afterPhase === "after-start"
              ? `Active after ${prayerName} start (+${secondsAfterStart}s)`
              : `Active after ${prayerName} end`,
          nextEvaluationTime: windowEnd,
        };
      }
    }

    return {
      isActive: false,
      effectivePriority: playlist.priority,
      reason: "Not currently after any scheduled prayer",
    };
  }

  /**
   * Evaluate "between" prayer mode (same as PrayerWindow)
   */
  private evaluateBetween(
    playlist: Playlist,
    schedule: PrayerBasedSchedule,
    context: ScheduleContext,
  ): ScheduleEvaluationResult {
    // Jummah prayers only activate on Fridays (day === 5)
    if (
      (this.isJummahPrayer(schedule.startPrayer) ||
        this.isJummahPrayer(schedule.endPrayer)) &&
      context.currentTime.getDay() !== 5
    ) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Jummah prayers only active on Fridays",
      };
    }

    // Dhuhr prayers only activate on non-Fridays (Jummah replaces Dhuhr on Friday)
    if (
      (this.isDhuhrPrayer(schedule.startPrayer) ||
        this.isDhuhrPrayer(schedule.endPrayer)) &&
      context.currentTime.getDay() === 5
    ) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: "Dhuhr does not exist on Friday (Jummah replaces it)",
      };
    }

    const startPrayerTime = this.getPrayerTime(
      context.prayerTimes!,
      schedule.startPrayer,
    );
    const endPrayerTime = this.getPrayerTime(
      context.prayerTimes!,
      schedule.endPrayer,
    );

    if (!startPrayerTime || !endPrayerTime) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Could not resolve prayer times for ${schedule.startPrayer} or ${schedule.endPrayer}`,
      };
    }

    const windowStart = new Date(startPrayerTime);
    if (schedule.secondsBeforeStartPrayer) {
      windowStart.setSeconds(
        windowStart.getSeconds() - schedule.secondsBeforeStartPrayer,
      );
    }

    const endTime = new Date(endPrayerTime);

    const prayerLength = getPrayerLength(
      schedule.endPrayer,
      context.screenData,
    );
    endTime.setSeconds(endTime.getSeconds() + prayerLength);

    const isInWindow =
      context.currentTime >= windowStart && context.currentTime <= endTime;

    if (!isInWindow) {
      return {
        isActive: false,
        effectivePriority: playlist.priority,
        reason: `Outside prayer window (${schedule.startPrayer} to ${schedule.endPrayer})`,
      };
    }

    return {
      isActive: true,
      effectivePriority: playlist.priority,
      deactivationTime: endTime,
      reason: `Active between ${schedule.startPrayer} and ${schedule.endPrayer}`,
      nextEvaluationTime: endTime,
    };
  }

  private evaluateAdhanWindow(
    playlist: Playlist,
    schedule: PrayerBasedSchedule,
    context: ScheduleContext,
    phase: "during" | "after",
  ): ScheduleEvaluationResult {
    const displayDurationSeconds = Math.max(
      0,
      schedule.displayDurationSeconds || 0,
    );

    for (const prayerName of schedule.prayers) {
      const prayerTime = this.getPrayerTime(context.prayerTimes!, prayerName);
      if (!prayerTime) {
        continue;
      }

      // Jummah prayers only activate on Fridays (day === 5)
      if (
        this.isJummahPrayer(prayerName) &&
        context.currentTime.getDay() !== 5
      ) {
        continue;
      }

      // Dhuhr prayers only activate on non-Fridays (Jummah replaces Dhuhr on Friday)
      if (
        this.isDhuhrPrayer(prayerName) &&
        context.currentTime.getDay() === 5
      ) {
        continue;
      }

      const adhanOffsetMinutes = this.getAdhanOffsetMinutes(
        prayerName,
        context.screenData,
      );
      const adhanDurationSeconds = this.getAdhanDurationSeconds(
        prayerName,
        context.screenData,
      );
      const adhanStart = new Date(prayerTime);
      adhanStart.setSeconds(adhanStart.getSeconds() - adhanOffsetMinutes * 60);

      const adhanEnd = new Date(adhanStart);
      adhanEnd.setSeconds(adhanEnd.getSeconds() + adhanDurationSeconds);

      if (phase === "during") {
        const effectiveEnd =
          displayDurationSeconds > 0
            ? new Date(adhanStart.getTime() + displayDurationSeconds * 1000)
            : adhanEnd;

        if (
          context.currentTime >= adhanStart &&
          context.currentTime <= effectiveEnd
        ) {
          return {
            isActive: true,
            effectivePriority: playlist.priority,
            deactivationTime: effectiveEnd,
            reason: `Active during adhan for ${prayerName}`,
            nextEvaluationTime: effectiveEnd,
          };
        }

        continue;
      }

      if (adhanEnd >= prayerTime) {
        continue;
      }

      const effectiveEnd =
        displayDurationSeconds > 0
          ? new Date(adhanEnd.getTime() + displayDurationSeconds * 1000)
          : prayerTime;

      if (
        context.currentTime >= adhanEnd &&
        context.currentTime <= effectiveEnd
      ) {
        return {
          isActive: true,
          effectivePriority: playlist.priority,
          deactivationTime: effectiveEnd,
          reason: `Active after adhan for ${prayerName}`,
          nextEvaluationTime: effectiveEnd,
        };
      }
    }

    return {
      isActive: false,
      effectivePriority: playlist.priority,
      reason:
        phase === "during"
          ? "Not currently during any configured adhan window"
          : "Not currently in any after-adhan window",
    };
  }

  /**
   * Get next state change time
   */
  getNextStateChange(
    playlist: Playlist,
    context: ScheduleContext,
  ): Date | null {
    const schedule = playlist.schedule;

    if (!schedule || !isPrayerBasedSchedule(schedule) || !context.prayerTimes) {
      return null;
    }

    // Find next upcoming prayer window
    const upcomingWindows: Date[] = [];

    if (
      schedule.prayerScheduleType === "before" ||
      this.isAfterScheduleType(schedule.prayerScheduleType) ||
      schedule.prayerScheduleType === "during-adhan" ||
      schedule.prayerScheduleType === "after-adhan"
    ) {
      for (const prayerName of schedule.prayers) {
        const prayerTime = this.getPrayerTime(context.prayerTimes, prayerName);
        if (!prayerTime) continue;
        if (
          this.isJummahPrayer(prayerName) &&
          context.currentTime.getDay() !== 5
        )
          continue;
        if (
          this.isDhuhrPrayer(prayerName) &&
          context.currentTime.getDay() === 5
        )
          continue;

        if (schedule.prayerScheduleType === "before") {
          const windowStart = new Date(prayerTime);
          windowStart.setSeconds(
            windowStart.getSeconds() - (schedule.secondsBeforeStartPrayer || 0),
          );
          if (windowStart > context.currentTime) {
            upcomingWindows.push(windowStart);
          }
        } else if (this.isAfterScheduleType(schedule.prayerScheduleType)) {
          const afterPhase = schedule.afterPhase || "after-end";
          const secondsAfterStart = schedule.secondsAfterStartPrayer || 0;
          const windowStart = this.getAfterWindowStart(
            prayerTime,
            prayerName,
            afterPhase,
            secondsAfterStart,
            context,
          );
          if (windowStart > context.currentTime) {
            upcomingWindows.push(windowStart);
          }
        } else if (schedule.prayerScheduleType === "during-adhan") {
          const displayDurationSeconds = Math.max(
            0,
            schedule.displayDurationSeconds || 0,
          );
          const adhanOffsetMinutes = this.getAdhanOffsetMinutes(
            prayerName,
            context.screenData,
          );
          const adhanDurationSeconds = this.getAdhanDurationSeconds(
            prayerName,
            context.screenData,
          );
          const adhanStart = new Date(prayerTime);
          adhanStart.setSeconds(
            adhanStart.getSeconds() - adhanOffsetMinutes * 60,
          );
          const effectiveEnd = new Date(
            adhanStart.getTime() +
              (displayDurationSeconds > 0
                ? displayDurationSeconds
                : adhanDurationSeconds) *
                1000,
          );

          if (adhanStart > context.currentTime) {
            upcomingWindows.push(adhanStart);
          } else if (effectiveEnd > context.currentTime) {
            upcomingWindows.push(effectiveEnd);
          }
        } else if (schedule.prayerScheduleType === "after-adhan") {
          const adhanOffsetMinutes = this.getAdhanOffsetMinutes(
            prayerName,
            context.screenData,
          );
          const adhanDurationSeconds = this.getAdhanDurationSeconds(
            prayerName,
            context.screenData,
          );
          const adhanStart = new Date(prayerTime);
          adhanStart.setSeconds(
            adhanStart.getSeconds() - adhanOffsetMinutes * 60,
          );
          const adhanEnd = new Date(adhanStart);
          adhanEnd.setSeconds(adhanEnd.getSeconds() + adhanDurationSeconds);
          const displayDurationSeconds = Math.max(
            0,
            schedule.displayDurationSeconds || 0,
          );
          const effectiveEnd =
            displayDurationSeconds > 0
              ? new Date(adhanEnd.getTime() + displayDurationSeconds * 1000)
              : prayerTime;
          if (adhanEnd > context.currentTime) {
            upcomingWindows.push(effectiveEnd);
          }
        }
      }
    }

    if (upcomingWindows.length === 0) {
      return null;
    }

    return upcomingWindows.sort((a, b) => a.getTime() - b.getTime())[0];
  }

  /**
   * Validate configuration
   */
  validateConfiguration(playlist: Playlist): string[] {
    const errors: string[] = [];
    const schedule = playlist.schedule;

    if (!schedule || !isPrayerBasedSchedule(schedule)) {
      errors.push("PrayerBased schedule configuration is missing or invalid");
      return errors;
    }

    const prayerBasedSchedule = schedule as PrayerBasedSchedule;

    // Validate schedule type
    if (prayerBasedSchedule.type !== ScheduleType.PrayerBased) {
      errors.push(
        `Schedule type mismatch: expected ${ScheduleType.PrayerBased}, got ${prayerBasedSchedule.type}`,
      );
    }

    // ✅ Only require daysOfWeek for "between" mode
    if (
      schedule.prayerScheduleType === "between" &&
      (!schedule.daysOfWeek || schedule.daysOfWeek.length === 0)
    ) {
      errors.push(
        "At least one day of the week must be selected for 'between' mode",
      );
    }

    // Validate prayer schedule type
    if (
      ![
        "before",
        "after",
        "after-start",
        "after-end",
        "during-adhan",
        "after-adhan",
        "between",
      ].includes(schedule.prayerScheduleType)
    ) {
      errors.push(
        `Invalid prayer schedule type: ${schedule.prayerScheduleType}`,
      );
    }

    // Validate prayers array
    if (
      schedule.prayerScheduleType !== "between" &&
      (!schedule.prayers || schedule.prayers.length === 0)
    ) {
      errors.push(
        "At least one prayer must be selected for before/after modes",
      );
    }

    // Validate between mode specific fields
    if (schedule.prayerScheduleType === "between") {
      if (!schedule.startPrayer) {
        errors.push("Start prayer is required for between mode");
      }
      if (!schedule.endPrayer) {
        errors.push("End prayer is required for between mode");
      }
    }

    // Validate durations
    if (
      schedule.displayDurationSeconds &&
      schedule.displayDurationSeconds < 0
    ) {
      errors.push("Display duration cannot be negative");
    }

    if (
      this.isAfterScheduleType(schedule.prayerScheduleType) &&
      schedule.afterPhase === "after-start" &&
      (schedule.secondsAfterStartPrayer || 0) < 0
    ) {
      errors.push("Seconds after prayer start cannot be negative");
    }

    return errors;
  }

  private getAdhanOffsetMinutes(prayerName: string, screenData: any): number {
    if (!screenData) return 0;

    if (prayerName === "JamFajr" || prayerName === "StartFajr") {
      return screenData.fajrAdhanOffset || 0;
    }
    if (prayerName === "JamDhuhr" || prayerName === "StartDhuhr") {
      return screenData.dhuhrAdhanOffset || 0;
    }
    if (prayerName === "JamAsar" || prayerName === "StartAsr") {
      return screenData.asarAdhanOffset || 0;
    }
    if (prayerName === "JamMaghrib" || prayerName === "StartMaghrib") {
      return screenData.maghribAdhanOffset || 0;
    }
    if (prayerName === "JamEsha" || prayerName === "StartEsha") {
      return screenData.ishaAdhanOffset || 0;
    }
    if (prayerName.startsWith("Jummah")) {
      return screenData.jummahAdhanOffset || 0;
    }

    return 0;
  }

  private getAdhanDurationSeconds(prayerName: string, screenData: any): number {
    if (!screenData) return 0;

    if (this.isJummahPrayer(prayerName)) {
      return Math.max(
        0,
        screenData.jummahAdhanDurationSeconds ||
          screenData.adhanDurationSeconds ||
          0,
      );
    }

    return Math.max(0, screenData.adhanDurationSeconds || 0);
  }

  private isJummahPrayer(prayerName: string): boolean {
    return prayerName?.startsWith("Jummah") ?? false;
  }

  private isDhuhrPrayer(prayerName: string): boolean {
    return prayerName === "StartDhuhr" || prayerName === "JamDhuhr";
  }

  private isAfterScheduleType(prayerScheduleType: string): boolean {
    return (
      prayerScheduleType === "after" ||
      prayerScheduleType === "after-start" ||
      prayerScheduleType === "after-end"
    );
  }

  private getAfterWindowStart(
    prayerTime: Date,
    prayerName: string,
    afterPhase: string,
    secondsAfterStart: number,
    context: ScheduleContext,
  ): Date {
    const windowStart = new Date(prayerTime);

    if (afterPhase === "after-start") {
      windowStart.setSeconds(windowStart.getSeconds() + secondsAfterStart);
      return windowStart;
    }

    const prayerLength = getPrayerLength(prayerName, context.screenData);
    const blankOffsetMinutes = this.isBlankingEnabledForPrayer(
      prayerName,
      context.screenData,
    )
      ? this.getBlankOffsetMinutes(prayerName, context.screenData)
      : 0;
    windowStart.setSeconds(
      windowStart.getSeconds() + prayerLength + blankOffsetMinutes * 60,
    );
    return windowStart;
  }

  private isBlankingEnabledForPrayer(
    prayerName: string,
    screenData: any,
  ): boolean {
    if (!screenData) return false;

    if (prayerName === "JamFajr" || prayerName === "StartFajr") {
      return !!screenData.blankScreenDuringFajr;
    }
    if (prayerName === "JamDhuhr" || prayerName === "StartDhuhr") {
      return !!screenData.blankScreenDuringDhuhr;
    }
    if (prayerName === "JamAsar" || prayerName === "StartAsr") {
      return !!screenData.blankScreenDuringAsar;
    }
    if (prayerName === "JamMaghrib" || prayerName === "StartMaghrib") {
      return !!screenData.blankScreenDuringMaghrib;
    }
    if (prayerName === "JamEsha" || prayerName === "StartEsha") {
      return !!screenData.blankScreenDuringIsha;
    }
    if (prayerName.startsWith("Jummah")) {
      return !!screenData.blankScreenDuringJummah;
    }

    return false;
  }

  private getBlankOffsetMinutes(prayerName: string, screenData: any): number {
    if (!screenData) return 0;

    if (prayerName === "JamFajr" || prayerName === "StartFajr") {
      return screenData.fajrBlankStartOffset || 0;
    }
    if (prayerName === "JamDhuhr" || prayerName === "StartDhuhr") {
      return screenData.dhuhrBlankStartOffset || 0;
    }
    if (prayerName === "JamAsar" || prayerName === "StartAsr") {
      return screenData.asarBlankStartOffset || 0;
    }
    if (prayerName === "JamMaghrib" || prayerName === "StartMaghrib") {
      return screenData.maghribBlankStartOffset || 0;
    }
    if (prayerName === "JamEsha" || prayerName === "StartEsha") {
      return screenData.ishaBlankStartOffset || 0;
    }
    if (prayerName.startsWith("Jummah")) {
      return screenData.jummahBlankStartOffset || 0;
    }

    return 0;
  }

  // ============================================================================
  // HELPER METHODS
  // ============================================================================

  private getPrayerTime(prayerTimes: any, prayerName: string): Date | null {
    const mapping: Record<string, string> = {
      StartFajr: "BegFajr",
      JamFajr: "JamFajr",
      StartSunrise: "Sunrise",
      StartDhuhr: "BegDhuhr",
      JamDhuhr: "JamDhuhr",
      StartAsr: "BegAsar",
      JamAsar: "JamAsar",
      StartMaghrib: "BegMaghrib",
      JamMaghrib: "JamMaghrib",
      StartEsha: "BegEsha",
      JamEsha: "JamEsha",
      Jummah1: "Jummah1",
      Jummah2: "Jummah2",
      Jummah3: "Jummah3",
      Jummah4: "Jummah4",
    };

    const key = mapping[prayerName];
    if (!key || !prayerTimes[key]) {
      return null;
    }

    const timeStr = prayerTimes[key];
    if (!timeStr || typeof timeStr !== "string") {
      return null;
    }

    // Validate time string format (HH:MM)
    const timeParts = timeStr.split(":");
    if (timeParts.length !== 2) {
      return null;
    }

    const hours = parseInt(timeParts[0], 10);
    const minutes = parseInt(timeParts[1], 10);

    // Validate hours and minutes are valid numbers and in range
    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    try {
      const today = new Date();
      const prayerDate = new Date(today);
      prayerDate.setHours(hours, minutes, 0, 0);

      // Additional validation: ensure the date was set correctly
      if (
        prayerDate.getHours() !== hours ||
        prayerDate.getMinutes() !== minutes
      ) {
        return null;
      }

      return prayerDate;
    } catch (error) {
      // If Date constructor or setHours fails, return null
      return null;
    }
  }

  private getDayName(day: number): string {
    const days = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    return days[day] || "Unknown";
  }
}
